import type { ReactNode } from 'react';
import { useCrmLocale } from '../../ui/locale/crmLocale';
import { SearchableDropdown } from './SearchableDropdown';
import './PageControls.css';

export interface PageControlsProps {
  page: number;
  hasMore?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onChange: (page: number) => void;
  previousLabel?: string;
  nextLabel?: string;
  pageLabel?: string;
  ariaLabel?: string;
  totalPages?: number;
  totalRecords?: number;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  variant?: 'previous-next' | 'numbered';
  showFirstLast?: boolean;
  hideWhenSinglePage?: boolean;
  pageSummary?: (page: number, totalPages: number | undefined) => ReactNode;
  className?: string;
}

export function PageControls({ page, hasMore, loading = false, disabled = false, onChange,
  previousLabel, nextLabel, pageLabel, ariaLabel, totalPages, totalRecords, pageSize,
  pageSizeOptions = [10, 25, 50, 100], onPageSizeChange, variant = 'previous-next',
  showFirstLast = false, hideWhenSinglePage = true, pageSummary, className = '' }: PageControlsProps) {
  const { t } = useCrmLocale();
  const pages = totalPages ?? (totalRecords !== undefined && pageSize && pageSize > 0 ? Math.ceil(totalRecords / pageSize) : undefined);
  const next = hasMore ?? (pages !== undefined && page < pages);
  const blocked = loading || disabled;
  const sizeControl = Boolean(pageSize && onPageSizeChange);
  if (hideWhenSinglePage && page === 1 && !next && totalRecords === undefined && !sizeControl) return null;
  const go = (target: number) => { if (!blocked && target !== page && target >= 1 && (pages === undefined || target <= pages)) onChange(target); };
  const firstLabel = t('First page');
  const lastLabel = t('Last page');
  const sizes = [...new Set([...pageSizeOptions.filter(size => size > 0 && Number.isInteger(size)), ...(pageSize ? [pageSize] : [])])].sort((a, b) => a - b);
  const numbered = variant === 'numbered' && pages !== undefined && pages > 0;
  const numbers = numbered ? [...new Set([1, ...Array.from({ length: 5 }, (_, i) => page - 2 + i), pages])].filter(value => value >= 1 && value <= pages).sort((a, b) => a - b) : [];
  return <nav aria-label={ariaLabel ?? (t("Pagination"))} aria-busy={loading} className={`crm-page-controls ${className}`}>
    {showFirstLast && pages !== undefined && <button type="button" disabled={blocked || page <= 1} onClick={() => go(1)}>{firstLabel}</button>}
    <button type="button" disabled={blocked || page <= 1} onClick={() => go(page - 1)}>{previousLabel ?? t('Previous')}</button>
    {numbered ? numbers.map((number, index) => <span key={number}>
      {index > 0 && number - numbers[index - 1] > 1 && <span className="crm-page-controls__gap" aria-hidden="true">…</span>}
      <button type="button" aria-label={`${pageLabel ?? t('Page')} ${number}`} aria-current={number === page ? 'page' : undefined}
        disabled={blocked} onClick={() => go(number)}>{number}</button>
    </span>) : <span aria-current="page">{pageSummary ? pageSummary(page, pages) : <>{pageLabel ?? t('Page')} {page}{pages !== undefined && <> {t('of')} {pages}</>}</>}</span>}
    <button type="button" disabled={blocked || !next} onClick={() => go(page + 1)}>{nextLabel ?? t('Next')}</button>
    {showFirstLast && pages !== undefined && <button type="button" disabled={blocked || page >= pages} onClick={() => go(pages)}>{lastLabel}</button>}
    {totalRecords !== undefined && <span>{t("Total records:")} {totalRecords}</span>}
    {sizeControl && <div className="crm-page-controls__size"><SearchableDropdown ariaLabel={t("Page size")}
      clearable={false} disabled={blocked} value={String(pageSize)}
      options={sizes.map(size => ({ value: String(size), label: String(size) }))}
      onChange={value => { const size = Number(value); if (!blocked && size !== pageSize && sizes.includes(size)) onPageSizeChange?.(size); }} /></div>}
  </nav>;
}
